import * as THREE from 'three'
import type { Floor } from '../types'

const FLOOR_HEIGHT = 3.0
const FLOOR_SIZE = 40
const LERP_SPEED = 5

export class FloorRenderer {
  group: THREE.Group
  index: number
  baseY: number
  targetY: number
  planeMesh: THREE.Mesh | null = null
  private edges: THREE.Line | null = null
  private label: THREE.Sprite | null = null
  private textureLoader = new THREE.TextureLoader()

  constructor(floor: Floor, floorplanUrl?: string) {
    this.index = floor.index
    this.baseY = floor.index * FLOOR_HEIGHT
    this.targetY = this.baseY

    this.group = new THREE.Group()
    this.group.position.y = this.baseY
    this.group.name = `floor-${floor.index}`
    this.group.userData = { floorIndex: floor.index, floorId: floor.id }

    this.createPlane(floorplanUrl)
    this.createLabel(floor.name)
  }

  private createPlane(floorplanUrl?: string) {
    const geometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE)
    geometry.rotateX(-Math.PI / 2)

    let material: THREE.Material
    if (floorplanUrl) {
      const texture = this.textureLoader.load(floorplanUrl)
      material = new THREE.MeshPhongMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.9,
      })
    } else {
      material = new THREE.MeshPhongMaterial({
        color: 0x222244,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.5,
      })
    }

    this.planeMesh = new THREE.Mesh(geometry, material)
    this.planeMesh.receiveShadow = true
    this.planeMesh.name = `floor-plane-${this.index}`
    this.group.add(this.planeMesh)

    // Grid helper
    const grid = new THREE.GridHelper(FLOOR_SIZE, FLOOR_SIZE, 0x333366, 0x222244)
    grid.material.transparent = true
    grid.material.opacity = 0.3
    this.group.add(grid)

    this.createEdges()
  }

  private createEdges() {
    const hw = FLOOR_SIZE / 2, hd = FLOOR_SIZE / 2
    const points = [
      new THREE.Vector3(-hw, 0.02, -hd),
      new THREE.Vector3(hw, 0.02, -hd),
      new THREE.Vector3(hw, 0.02, hd),
      new THREE.Vector3(-hw, 0.02, hd),
      new THREE.Vector3(-hw, 0.02, -hd),
    ]
    const geo = new THREE.BufferGeometry().setFromPoints(points)
    const mat = new THREE.LineBasicMaterial({ color: 0xe94560, transparent: true, opacity: 0.8 })
    this.edges = new THREE.Line(geo, mat)
    this.group.add(this.edges)
  }

  private createLabel(name: string) {
    const canvas = document.createElement('canvas')
    canvas.width = 256
    canvas.height = 64
    const ctx = canvas.getContext('2d')!
    ctx.fillStyle = '#e94560'
    ctx.font = 'bold 32px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(name.substring(0, 15), 128, 32)
    const texture = new THREE.CanvasTexture(canvas)
    const mat = new THREE.SpriteMaterial({ map: texture, transparent: true })
    this.label = new THREE.Sprite(mat)
    this.label.scale.set(8, 2, 1)
    this.label.position.set(-FLOOR_SIZE / 2 - 5, 0.5, 0)
    this.group.add(this.label)
  }

  setFloorplanTexture(url: string) {
    if (!this.planeMesh) return
    const texture = this.textureLoader.load(url)
    const mat = this.planeMesh.material as THREE.MeshPhongMaterial
    mat.map = texture
    mat.opacity = 0.9
    mat.needsUpdate = true
  }

  setTargetY(y: number) {
    this.targetY = y
  }

  update(dt: number) {
    const diff = this.targetY - this.group.position.y
    if (Math.abs(diff) > 0.01) {
      this.group.position.y += diff * Math.min(LERP_SPEED * dt, 1)
    } else {
      this.group.position.y = this.targetY
    }
  }

  setVisible(visible: boolean) {
    this.group.visible = visible
  }

  setPlaneVisible(visible: boolean) {
    if (this.planeMesh) this.planeMesh.visible = visible
  }

  setOpacity(opacity: number) {
    if (this.planeMesh) {
      const mat = this.planeMesh.material as THREE.MeshPhongMaterial
      mat.opacity = opacity
    }
  }

  dispose() {
    if (this.edges) {
      this.edges.geometry.dispose()
      ;(this.edges.material as THREE.Material).dispose()
    }
    if (this.label) {
      ;(this.label.material as THREE.SpriteMaterial).map?.dispose()
      this.label.material.dispose()
    }
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose()
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose())
        } else {
          obj.material.dispose()
        }
      }
    })
  }
}
